import { createSelector } from 'reselect';

import { IFetchChecklistTemplatesParams } from 'APIPackage/AccountModule/services/checklistTemplatesApi';
import { IFetchRequirementTemplatesParams } from 'APIPackage/AccountModule/services/requirementTemplatesApi';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { createUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/common/createUserOrganisationSelectOption';
import { createKnoOrganizationFunctionSelectOption } from 'CorePackage/CatalogsModule/common/createKnoOrganizationFunctionSelectOption';

import { createChecklistTemplateSelectOption } from '../common/createChecklistTemplateSelectOption';
import { createRequirementTemplateSelectOption } from '../common/createRequirementTemplateSelectOption';
import { createUserOrganizationWithDistrictSelectOption } from '../common/createUserOrganizationWithDistrictSelectOption';
import { getKnoOrganizationFunctionsGroupStoreKey } from '../common/getKnoOrganizationFunctionsGroupStoreKey';
import { getTemplatesGroupStoreKey } from '../common/getTemplatesGroupStoreKey';
import { AccountStatusesEnum } from '../enums/AccountStatusesEnum';

export const getAccountChecklistTemplatesItem = (state: IRootState, params: IFetchChecklistTemplatesParams) =>
  state.account.checklistTemplates[getTemplatesGroupStoreKey(params)];
export const getAccountChecklistTemplatesItemData = (state: IRootState, params: IFetchChecklistTemplatesParams) =>
  getAccountChecklistTemplatesItem(state, params)?.data;
export const getAccountChecklistTemplatesItemDataList = (state: IRootState, params: IFetchChecklistTemplatesParams) =>
  getAccountChecklistTemplatesItemData(state, params)?.list;
export const getAccountChecklistTemplatesItemStatus = (state: IRootState, params: IFetchChecklistTemplatesParams) =>
  getAccountChecklistTemplatesItem(state, params)?.status;
export const makeGetAccountChecklistTemplatesItemSelectOptions = () => {
  return createSelector(getAccountChecklistTemplatesItemDataList, templatesList =>
    templatesList?.map(item => createChecklistTemplateSelectOption(item)),
  );
};

export const getAccountRequirementTemplatesItem = (state: IRootState, params: IFetchRequirementTemplatesParams) =>
  state.account.requirementTemplates[getTemplatesGroupStoreKey(params)];
export const getAccountRequirementTemplatesItemData = (state: IRootState, params: IFetchRequirementTemplatesParams) =>
  getAccountRequirementTemplatesItem(state, params)?.data;
export const getAccountRequirementTemplatesItemDataList = (
  state: IRootState,
  params: IFetchRequirementTemplatesParams,
) => getAccountRequirementTemplatesItemData(state, params)?.list;
export const getAccountRequirementTemplatesItemStatus = (state: IRootState, params: IFetchRequirementTemplatesParams) =>
  getAccountRequirementTemplatesItem(state, params)?.status;
export const makeGetAccountRequirementTemplatesItemSelectOptions = () => {
  return createSelector(getAccountRequirementTemplatesItemDataList, templatesList =>
    templatesList?.map(item => createRequirementTemplateSelectOption(item)),
  );
};

export const getAccountOrganizationsWithDistrict = (state: IRootState) => state.account.organizationsWithDistrict;
export const getAccountOrganizationsWithDistrictData = (state: IRootState) =>
  getAccountOrganizationsWithDistrict(state).data;
export const getAccountOrganizationsWithDistrictStatus = (state: IRootState) =>
  getAccountOrganizationsWithDistrict(state).status;
export const getAccountOrganizationsWithDistrictIsLoading = (state: IRootState) => {
  const status: AccountStatusesEnum | undefined = getAccountOrganizationsWithDistrictStatus(state);

  return status === AccountStatusesEnum.LOADING;
};
export const getAccountKnoOrganizationsWithDistrictSelectOptions = createSelector(
  getAccountOrganizationsWithDistrictData,
  data => data.kno.map(item => createUserOrganisationSelectOption(item)),
);
export const getAccountProsecutorOrganizationsWithDistrictSelectOptions = createSelector(
  getAccountOrganizationsWithDistrictData,
  data => data.prosecutor.map(item => createUserOrganizationWithDistrictSelectOption(item)),
);

export const getAccountOrganizations = (state: IRootState) => state.account.organizations;
export const getAccountOrganizationsData = (state: IRootState) => getAccountOrganizations(state).data;
export const getAccountOrganizationsStatus = (state: IRootState) => getAccountOrganizations(state).status;
export const getAccountOrganizationsIsLoading = (state: IRootState) => {
  const status: AccountStatusesEnum | undefined = getAccountOrganizationsStatus(state);

  return status === AccountStatusesEnum.LOADING;
};
export const getAccountOrganizationsIsSuccess = (state: IRootState) => {
  const status: AccountStatusesEnum | undefined = getAccountOrganizationsStatus(state);

  return status === AccountStatusesEnum.SUCCEEDED;
};

export const getAccountKnoOrganizationsSelectOptions = createSelector(getAccountOrganizationsData, data =>
  data.kno.map(item => createUserOrganisationSelectOption(item)),
);

export const getAccountProsecutorOrganizationsSelectOptions = createSelector(getAccountOrganizationsData, data =>
  data.prosecutor.map(item => createUserOrganisationSelectOption(item)),
);

export const getAccountKnoOrganizationFunctionsGroup = (state: IRootState, knoOrganizationId: string) =>
  state.account.knoOrganizationFunctions[getKnoOrganizationFunctionsGroupStoreKey(knoOrganizationId)];
export const getAccountKnoOrganizationFunctionsGroupData = (state: IRootState, knoOrganizationId: string) =>
  getAccountKnoOrganizationFunctionsGroup(state, knoOrganizationId)?.data;
export const getAccountKnoOrganizationFunctionsGroupStatus = (state: IRootState, knoOrganizationId: string) =>
  getAccountKnoOrganizationFunctionsGroup(state, knoOrganizationId)?.status;
export const makeGetAccountKnoOrganizationFunctionsGroupSelectOptions = () => {
  return createSelector(getAccountKnoOrganizationFunctionsGroupData, knoOrganizationFunctions =>
    knoOrganizationFunctions?.map(item => createKnoOrganizationFunctionSelectOption(item)),
  );
};
export const getAccountKnoOrganizationFunctionsGroupSelectOptions = makeGetAccountKnoOrganizationFunctionsGroupSelectOptions();
export const getAccountRequirementTemplates = (state: IRootState) => state.account.requirementTemplates;
export const getAccountCheckListsTemplates = (state: IRootState) => state.account.checklistTemplates;
export const getAccountDefaultKnoOrganizationId = (state: IRootState) =>
  state.account.organizations.data.kno.find(item => item.byDefault)?.id;
